import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useEffect, useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  LoadingSkeleton,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  StatusChip,
  Typography,
} from '@munifocus/ui';
import type { FileStage, FileSummary } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { useFiles } from '../lib/files-api';
import { statusChipKind, stageLabelKey } from '../lib/file-display';
import { getTileConfig, MUNICIPALITY_ZOOM, NB_BOUNDS } from '../lib/map-tiles';
import { useActiveScopeCentre } from '../lib/map-centre';
import { googleDirectionsUrl } from '../lib/directions';
import { useHazardMap } from '../lib/safety-api';
import { DirectionsLink, kindsToLines } from '../components/safety/HazardWarning';

// Pin colour per enforcement stage — distinct hues so the officer reads the
// field at a glance. Emergency files override this (see pinColor).
const STAGE_COLOR: Record<FileStage, string> = {
  intake: palette.semantic.pending,
  review: palette.brand.accent,
  site_visit: palette.semantic.warning,
  classification: palette.brand.rail,
  escalation: palette.semantic.emergency,
  closed: palette.semantic.closed,
};

const LEGEND_STAGES: FileStage[] = [
  'intake',
  'review',
  'site_visit',
  'classification',
  'escalation',
];

function pinColor(file: FileSummary): string {
  if (file.status === 'emergency') return palette.semantic.emergency;
  return STAGE_COLOR[file.stage] ?? palette.semantic.closed;
}

/** A teardrop map pin rendered as inline SVG, so we don't ship Leaflet's
 * default marker PNGs (which break under bundlers) and we get per-stage colour. */
function pinIcon(color: string, hazard = false): L.DivIcon {
  // A hazard pin keeps its stage colour and gains a thick emergency ring and an
  // exclamation mark in place of the dot, so it reads at a glance and without
  // relying on colour alone.
  const mark = hazard
    ? `<circle cx="13" cy="13" r="7" fill="${palette.background.paper}" stroke="${palette.semantic.emergency}" stroke-width="2.5"/>
       <rect x="12" y="8.5" width="2" height="5.5" rx="1" fill="${palette.semantic.emergency}"/>
       <circle cx="13" cy="16.6" r="1.2" fill="${palette.semantic.emergency}"/>`
    : `<circle cx="13" cy="13" r="5" fill="${palette.background.paper}"/>`;
  const svg = `
    <svg width="26" height="36" viewBox="0 0 26 36" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0C5.82 0 0 5.82 0 13c0 9.2 11.1 21.5 12.1 22.6a1.2 1.2 0 0 0 1.8 0C14.9 34.5 26 22.2 26 13 26 5.82 20.18 0 13 0z" fill="${color}"${hazard ? ` stroke="${palette.semantic.emergency}" stroke-width="2"` : ''}/>
      ${mark}
    </svg>`;
  return L.divIcon({
    html: svg,
    className: hazard ? 'gov-pin gov-pin-hazard' : 'gov-pin',
    iconSize: [26, 36],
    iconAnchor: [13, 36],
    popupAnchor: [0, -34],
  });
}

/**
 * Choose the view: the pins when there are any (a sane zoom for a single one),
 * otherwise the municipality the officer is working in. The map mounts on the
 * whole province, which is what stays on screen when neither is known.
 *
 * The empty case used to open on a hardcoded greater Moncton — right for the
 * demo accounts and wrong for every other municipality on the platform.
 */
function FitView({
  points,
  centre,
}: {
  points: Array<[number, number]>;
  centre: [number, number] | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [48, 48] });
      return;
    }
    if (centre) map.setView(centre, MUNICIPALITY_ZOOM);
  }, [map, points, centre]);
  return null;
}

export function MapView() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useFiles();

  const files = useMemo(() => (data?.items ?? []).filter((f) => f.status !== 'closed'), [data]);
  const pinned = useMemo(
    () => files.filter((f) => f.latitude != null && f.longitude != null),
    [files],
  );
  const points = useMemo(
    () => pinned.map((f) => [f.latitude as number, f.longitude as number] as [number, number]),
    [pinned],
  );
  const unmapped = files.length - pinned.length;
  const hazardMap = useHazardMap();
  const hazardsByFile = useMemo(
    () => new Map((hazardMap.data ?? []).map((h) => [h.fileId, kindsToLines(h.kinds)])),
    [hazardMap.data],
  );

  const centreQuery = useActiveScopeCentre();
  const centre = useMemo<[number, number] | null>(
    () => (centreQuery.data ? [centreQuery.data.latitude, centreQuery.data.longitude] : null),
    [centreQuery.data],
  );
  // Hold the map back until the municipality's centre is known, so an empty
  // caseload opens on its own town instead of flashing the province first.
  const loading = isLoading || centreQuery.isLoading;

  const tiles = getTileConfig();

  return (
    <AppShell>
      <Stack
        direction="row"
        sx={{ mb: 2, alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ display: { md: 'none' }, letterSpacing: '-0.01em' }}>
            {t('gov.map.title', { defaultValue: 'Field map' })}
          </Typography>
          <Typography sx={{ mt: 0.5, color: palette.text.secondary }}>
            {t('gov.map.subtitle', {
              defaultValue: 'Active files, pinned and colour-coded by stage.',
            })}
          </Typography>
        </Box>
      </Stack>

      {/* The map's own outline, at the map's own height, so the page does not
          jump when the pins land. */}
      {loading && <LoadingSkeleton variant="map" label={t('common.labels.loading')} />}

      {isError && !loading && (
        <HairlineCard sx={{ py: 6, px: 3, textAlign: 'center' }}>
          <Typography sx={{ color: palette.text.secondary, mb: 2 }}>
            {t('gov.map.loadError', { defaultValue: 'Could not load files for the map.' })}
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            {t('common.actions.retry', { defaultValue: 'Retry' })}
          </Button>
        </HairlineCard>
      )}

      {!loading && !isError && (
        <HairlineCard sx={{ overflow: 'hidden', p: 0 }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                height: { xs: 'calc(100vh - 260px)', md: 'calc(100vh - 220px)' },
                minHeight: 420,
                // Leaflet's own controls/attribution use system fonts; the map
                // tiles fill the card edge-to-edge.
                '& .leaflet-container': {
                  height: '100%',
                  width: '100%',
                  background: palette.background.parch,
                },
              }}
            >
              <MapContainer
                bounds={NB_BOUNDS}
                scrollWheelZoom
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url={tiles.url}
                  attribution={tiles.attribution}
                  maxZoom={tiles.maxZoom}
                />
                <FitView points={points} centre={centre} />
                {pinned.map((f) => {
                  const fileHazards = hazardsByFile.get(f.id) ?? [];
                  const directions = googleDirectionsUrl(f);
                  return (
                    <Marker
                      key={f.id}
                      position={[f.latitude as number, f.longitude as number]}
                      icon={pinIcon(pinColor(f), fileHazards.length > 0)}
                      title={
                        fileHazards.length
                          ? `${f.reference} — ${t('gov.safety.hazards.warning')}`
                          : f.reference
                      }
                    >
                      <Popup>
                        <Stack spacing={0.75} sx={{ minWidth: 180 }}>
                          <Stack
                            direction="row"
                            sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                          >
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                              {f.reference}
                            </Typography>
                            <StatusChip
                              status={statusChipKind(f.status)}
                              label={t(stageLabelKey(f.stage))}
                            />
                          </Stack>
                          <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>
                            {f.address}
                          </Typography>
                          {fileHazards.length > 0 && (
                            <Typography
                              role="note"
                              sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: palette.semantic.emergency,
                              }}
                            >
                              {t('gov.safety.hazards.warning')}:{' '}
                              {fileHazards
                                .map((h) => t(`gov.safety.hazards.kinds.${h.kind}`))
                                .join(', ')}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                            <Button
                              component={RouterLink}
                              to={`/files/${f.id}`}
                              size="small"
                              variant="text"
                              sx={{ alignSelf: 'flex-start', px: 0 }}
                            >
                              {t('gov.map.openFile', { defaultValue: 'Open file' })}
                            </Button>
                            {/* Hands the trip to Google Maps in a new tab. Nothing
                              about it is recorded here. */}
                            {directions && (
                              <DirectionsLink
                                href={directions}
                                address={f.address}
                                hazards={fileHazards}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </Box>

            {/* Stage legend */}
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1000,
                backgroundColor: palette.overlay.whiteSticky,
                backdropFilter: 'blur(6px)',
                border: `1px solid ${palette.border.line}`,
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                boxShadow: `0 4px 14px ${palette.overlay.cardShadow}`,
              }}
            >
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, color: palette.text.secondary, mb: 0.75 }}
              >
                {t('gov.map.legend', { defaultValue: 'Stage' })}
              </Typography>
              <Stack spacing={0.5}>
                {LEGEND_STAGES.map((stage) => (
                  <Stack key={stage} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: STAGE_COLOR[stage],
                      }}
                    />
                    <Typography sx={{ fontSize: 12, color: palette.text.primary }}>
                      {t(stageLabelKey(stage))}
                    </Typography>
                  </Stack>
                ))}
                {hazardsByFile.size > 0 && (
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: `2px solid ${palette.semantic.emergency}`,
                      }}
                    />
                    <Typography sx={{ fontSize: 12, color: palette.text.primary }}>
                      {t('gov.safety.hazards.title')}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Footer note: pins + any files we couldn't place */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              px: 2,
              py: 1.25,
              borderTop: `1px solid ${palette.border.line}`,
            }}
          >
            <Icons.MapPin size={iconSize.sm} color={palette.text.muted} />
            <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>
              {t('gov.map.pinnedCount', {
                defaultValue: '{{count}} files on the map',
                count: pinned.length,
              })}
            </Typography>
            {unmapped > 0 && (
              <Typography sx={{ fontSize: 13, color: palette.text.muted }}>
                ·{' '}
                {t('gov.map.unmappedCount', {
                  defaultValue: '{{count}} without a located address',
                  count: unmapped,
                })}
              </Typography>
            )}
          </Stack>
        </HairlineCard>
      )}
    </AppShell>
  );
}
